'use client'

import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/utils/twMerge'

export const MIN_QUANTITY = 1
export const MAX_QUANTITY = 99_999

const HOLD_DELAY_MS = 350
const HOLD_FASTEST_MS = 40

const clampQuantity = (n: number) => Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, n))

interface QuantityStepperProps {
  value: number
  onChange: (quantity: number) => void
  /** Product name, read out with each control */
  label: string
  className?: string
}

/**
 * The − / count / + control on quote cart lines. Holding a button keeps stepping and speeds up,
 * Shift+Arrow steps by ten, and the field can be cleared and retyped without snapping back to 1.
 */
export default function QuantityStepper({ value, onChange, label, className }: QuantityStepperProps) {
  // What the field shows while it is being edited; null means it mirrors `value`
  const [draft, setDraft] = useState<string | null>(null)
  // Held buttons and typed counts step from the latest value without re-arming their timers
  const valueRef = useRef(value)
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    valueRef.current = value
    onChangeRef.current = onChange
  })
  const holdTimer = useRef<number | null>(null)

  const stopHold = () => {
    if (holdTimer.current === null) return
    window.clearTimeout(holdTimer.current)
    holdTimer.current = null
  }
  useEffect(() => stopHold, [])

  // Returns false once the value is pinned at a bound, so a held button stops repeating there
  const step = (delta: number) => {
    const next = clampQuantity(valueRef.current + delta)
    if (next === valueRef.current) return false
    valueRef.current = next
    onChangeRef.current(next)
    return true
  }

  const startHold = (delta: number) => {
    stopHold()
    if (!step(delta)) return
    let interval = HOLD_DELAY_MS
    const repeat = () => {
      holdTimer.current = window.setTimeout(() => {
        if (!step(delta)) return stopHold()
        interval = Math.max(HOLD_FASTEST_MS, interval * 0.8)
        repeat()
      }, interval)
    }
    repeat()
  }

  // The count Escape puts back
  const valueAtFocus = useRef(value)

  // Typed counts are already saved as they land, so leaving the field only drops the draft;
  // a blank or 0 field goes back to the last count, since Remove is the way to drop a line
  const commitDraft = () => setDraft(null)

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    // A fractional count is left unsaved rather than having its digits read as a different number
    if (!/^\d*$/.test(raw)) return
    setDraft(raw)
    // Re-price live as soon as the field holds a usable count; blank or 0 waits for blur
    const parsed = parseInt(raw, 10)
    if (parsed >= MIN_QUANTITY) step(clampQuantity(parsed) - valueRef.current)
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      step(valueAtFocus.current - valueRef.current)
      setDraft(null)
      e.currentTarget.blur()
    } else if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault()
      setDraft(null)
      step(e.key === 'ArrowUp' ? 10 : -10)
    }
  }

  const holdFromButton = (delta: number) => {
    setDraft(null)
    startHold(delta)
  }

  return (
    <div
      className={cn(
        'inline-flex shrink-0 items-stretch overflow-hidden rounded-md border border-primary-ink-10 bg-white transition-colors focus-within:border-primary-ink-100',
        className,
      )}>
      <StepButton
        delta={-1}
        disabled={value <= MIN_QUANTITY}
        label={`Decrease quantity for ${label}`}
        onStep={step}
        onHold={holdFromButton}
        onRelease={stopHold}
      />
      <input
        type="number"
        inputMode="numeric"
        min={MIN_QUANTITY}
        max={MAX_QUANTITY}
        value={draft ?? value}
        onChange={onInputChange}
        onKeyDown={onInputKeyDown}
        onBlur={commitDraft}
        onFocus={(e) => {
          valueAtFocus.current = value
          e.currentTarget.select()
        }}
        aria-label={`Quantity for ${label}`}
        className="h-8 w-14 border-x border-y-0 px-1 border-primary-ink-10 bg-transparent text-center text-sm tabular-nums text-primary-ink-100 [appearance:textfield] focus:border-primary-ink-10 focus:outline-none focus:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <StepButton
        delta={1}
        disabled={value >= MAX_QUANTITY}
        label={`Increase quantity for ${label}`}
        onStep={step}
        onHold={holdFromButton}
        onRelease={stopHold}
      />
      <span className="sr-only" aria-live="polite">
        {value} {value === 1 ? 'license' : 'licenses'}
      </span>
    </div>
  )
}

interface StepButtonProps {
  delta: 1 | -1
  disabled: boolean
  label: string
  onStep: (delta: number) => void
  onHold: (delta: number) => void
  onRelease: () => void
}

function StepButton({ delta, disabled, label, onStep, onHold, onRelease }: StepButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={label}
      onPointerDown={(e) => {
        if (e.button === 0 && !disabled) onHold(delta)
      }}
      onPointerUp={onRelease}
      onPointerLeave={onRelease}
      onPointerCancel={onRelease}
      // Pointer presses are handled above; a keyboard activation arrives as a click with detail 0
      onClick={(e) => {
        if (e.detail === 0) onStep(delta)
      }}
      className="flex h-8 w-8 touch-manipulation select-none items-center justify-center text-primary-ink-85 transition-colors hover:bg-primary-ink-6 active:bg-primary-ink-10 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent">
      {delta > 0 ? '+' : '−'}
    </button>
  )
}
